interface BlogPost {
  id: string;
  title: string;
  description: string;
  publishDate: string;
  formattedDate: string;
  imageUrl: string | null;
  url: string;
}

interface Frontmatter {
  image?: string | null;
  title: string;
  description: string;
  date: string;
}

/**
 * Get a list of all blog post directories
 * In a real implementation, this would come from an API
 * For this demo, we'll hardcode the folders
 */
const getBlogFolders = async (): Promise<string[]> => {
  // In a real app, this would fetch the list of folders from an API or using file system
  // For this demo implementation, we'll hardcode the available blog folders
  return [
    'authentic-thai-dishes',
    'authentic-thai-flavors-seattle',
    'traditional-thai-cooking-techniques',
    'mothers-day-celebration',
    'thai-tapas-menu',
    'mango-sticky-rice'
  ];
};

/**
 * Dynamically check for any image file in the blog post folder
 * Returns the path to the first image found, or null if none exists
 */
const findBlogImage = async (folderName: string): Promise<string | null> => {
  try {
    // In a production environment, this would use file system operations
    // For our client-side implementation, we'll try common image extensions
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    
    // First check for any image with common names
    const commonNames = ['image', 'featured', 'thumbnail', 'cover', 'header', 'main'];
    
    for (const name of commonNames) {
      for (const ext of imageExtensions) {
        const path = `/blog-posts/${folderName}/${name}${ext}`;
        
        try {
          // Check if this file exists by attempting to fetch it
          const response = await fetch(path, { method: 'HEAD' });
          if (response.ok) {
            return path;
          }
        } catch (e) {
          // Ignore errors and continue trying other combinations
        }
      }
    }
    
    // If no common names found, try to find any image file in the folder
    // For demo purposes, we'll check for IMG_ prefix (common in many cameras)
    for (const ext of imageExtensions) {
      const imgPattern = `/blog-posts/${folderName}/IMG_`;
      try {
        // Since we can't list directory contents from the browser,
        // we'll have a specific attempt for IMG_* files which are common
        const response = await fetch(`${imgPattern}2415${ext}`, { method: 'HEAD' });
        if (response.ok) {
          return `${imgPattern}2415${ext}`;
        }
      } catch (e) {
        // Ignore errors and continue
      }
    }
    
    // Fallback to the original expected path
    return `/blog-posts/${folderName}/image.jpg`;
    
  } catch (error) {
    console.error(`Error finding image for blog post ${folderName}:`, error);
    return null;
  }
};

/**
 * Extracts blog post metadata from HTML content
 */
export const extractBlogMetadata = async (content: string, id: string): Promise<BlogPost | null> => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    
    // Extract metadata
    const title = doc.querySelector("title")?.textContent || "";
    const description = doc.querySelector('meta[name="description"]')?.getAttribute("content") || "";
    const publishedTime = doc.querySelector('meta[property="article:published_time"]')?.getAttribute("content") || "";
    
    // Check for image in meta tags first
    let imageUrl = doc.querySelector('meta[property="og:image"]')?.getAttribute("content") || null;
    
    // If no image URL in meta tags or it's the default path, try to find an image in the folder
    if (!imageUrl || imageUrl.includes('/image.jpg')) {
      imageUrl = await findBlogImage(id);
    }
    
    // Format date for display
    let formattedDate = "";
    if (publishedTime) {
      const date = new Date(publishedTime);
      formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else {
      // Try to find date in the article content
      const dateText = doc.querySelector('article p')?.textContent || "";
      if (dateText.includes("Published:")) {
        formattedDate = dateText.replace("Published:", "").trim();
      }
    }
    
    return {
      id,
      title,
      description,
      publishDate: publishedTime,
      formattedDate,
      imageUrl,
      url: `/blog/${id}`
    };
  } catch (error) {
    console.error("Error parsing blog post:", error);
    return null;
  }
};

/**
 * Fetches all blog posts
 */
export const fetchBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    // Get list of blog folders
    const blogFolders = await getBlogFolders();
    
    const posts = await Promise.all(
      blogFolders.map(async (folderName) => {
        try {
          // Fetch content.html from each folder
          const response = await fetch(`/blog-posts/${folderName}/content.html`);
          if (!response.ok) throw new Error(`Failed to fetch content from ${folderName}`);
          
          const content = await response.text();
          return await extractBlogMetadata(content, folderName);
        } catch (error) {
          console.error(`Error fetching blog post ${folderName}:`, error);
          return null;
        }
      })
    );
    
    // Filter out null entries and sort by publish date (newest first)
    return posts
      .filter((post): post is BlogPost => post !== null)
      .sort((a, b) => {
        // Sort by date (newest first)
        return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
      });
  } catch (error) {
    console.error("Error fetching blog posts:", error);
    return [];
  }
};

/**
 * Fetches a single blog post by ID
 */
export const fetchBlogPost = async (id: string): Promise<{
  metadata: BlogPost | null;
  content: string | null;
}> => {
  try {
    // Fetch the content.html file from the folder
    const response = await fetch(`/blog-posts/${id}/content.html`);
    if (!response.ok) throw new Error(`Failed to fetch blog post: ${id}`);
    
    const content = await response.text();
    const metadata = await extractBlogMetadata(content, id);
    
    // Extract the article content
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    const articleElement = doc.querySelector('article');
    
    return {
      metadata,
      content: articleElement?.outerHTML || null
    };
  } catch (error) {
    console.error(`Error fetching blog post ${id}:`, error);
    return {
      metadata: null,
      content: null
    };
  }
};

export const getPostImageUrl = (folderName: string): string => {
  // Prefer .webp if available in the optimized folder
  return `/blog-posts/${folderName}/optimized/image.webp`;
};

export const getBlogContent = async (folderName: string): Promise<string | null> => {
  try {
    const htmlPath = `/blog-posts/${folderName}/content.html`;
    const response = await fetch(htmlPath);
    if (!response.ok) throw new Error(`Failed to fetch ${htmlPath}`);
    let htmlContent = await response.text();

    // Replace image.jpg or image.png etc. with image.webp from optimized folder
    // This regex looks for src="/blog-posts/ANY_FOLDER_NAME/image.(jpg|jpeg|png)"
    // and replaces it with src="/blog-posts/ANY_FOLDER_NAME/optimized/image.webp"
    // It also handles cases where the path might already include /optimized/
    // and ensures that a direct image.jpg (etc) at the root of a post folder is also caught.
    htmlContent = htmlContent.replace(
      /src="(\/blog-posts\/[^\/]+\/)(?:optimized\/)?(image\.(?:jpg|jpeg|png|JPG|JPEG|PNG))"/g,
      'src="$1optimized/image.webp"'
    );
    // A simpler regex for cases where it's just image.jpg or similar at the end of a path
    htmlContent = htmlContent.replace(
      /(image\.(?:jpg|jpeg|png|JPG|JPEG|PNG))"/g,
      'optimized/image.webp"'
    );

    return htmlContent;
  } catch (error) {
    console.error("Error fetching blog content:", error);
    return null;
  }
};

// Update this function to look for optimized images first
export const getPostData = (folderName: string, frontmatter: Frontmatter): BlogPost => {
  let imageUrl = frontmatter.image || getPostImageUrl(folderName); // Use getPostImageUrl

  // If the auto-generated URL is used and it points to a non-webp, try to correct it
  // This is a bit of a fallback if frontmatter.image is not set or is incorrect
  if (imageUrl.includes('/image.jpg') || imageUrl.includes('/image.jpeg') || imageUrl.includes('/image.png')) {
    imageUrl = `/blog-posts/${folderName}/optimized/image.webp`;
  }

  return {
    id: folderName,
    title: frontmatter.title,
    description: frontmatter.description,
    publishDate: frontmatter.date,
    formattedDate: frontmatter.date,
    imageUrl,
    url: `/blog/${folderName}`
  };
}; 